// GENERATED by helm-console/scripts/ensure-themes.mjs — DO NOT EDIT.
// Source of truth: helm-bridge/src/client/styles.ts (policed by
// helm-bridge/scripts/perf-budget.mjs + the Bridge smoke's style drift guard).
// Regenerated on every console build; edits here are overwritten.
// Copied VERBATIM — styles.ts has no imports at all.
// styles.ts — the STYLE registry: the second theming axis.
//
// Two axes, one look (owner ruling 2026-09-05, Styles × Themes program §0):
//   STYLE  = the MATERIAL system — how surfaces are made. Background grade,
//            transparency, refraction, inner highlight, glow and shadow
//            grammar, per SURFACE TIER (shell / card / popover / modal).
//   THEME  = a palette living inside a style (themes.ts). Unchanged: 20
//            themes, two built-ins, one pick per app mode.
// Style is ONE GLOBAL pick; the dark/light theme slots live within it.
//
// `blueprint` is the IDENTITY style: its material tokens resolve to exactly
// what the app renders today. Every value in the styles.css block that backs
// it is a `var()` reference to the token that tier ALREADY consumes — there
// is not one new literal, so the identity cannot drift by construction, and
// scripts/style-identity.mjs measures it anyway.
//
// HARD CONSTRAINT (mirrors themes.ts, and for the same reason): this module
// must stay VALUE-IMPORT-FREE (`import type` only) and DOM-FREE. smoke.mjs
// and scripts/perf-budget.mjs transpile it standalone with esbuild and import
// the temp module directly, so any runtime import would fail to resolve —
// that failure is the guard, keep it. The DOM half lives in style-axis.ts.

/** How a style paints the z-0 ground behind everything.
 *
 *   flat      — one theme-tinted color. CSS only.
 *   gradient  — a CSS gradient. CSS only. (Blueprint is here: the fixed
 *               radial the shell has always painted.)
 *   texture   — gradient + a tiling noise/texture overlay. Inserts the
 *               single background LAYER element; no animation, no rAF.
 *   canvas    — an animated canvas behind the shell. Inserts the layer
 *               element AND the one canvas. rAF, paused on a hidden tab, and
 *               refused outright under prefers-reduced-motion /
 *               prefers-reduced-transparency (degrades to `gradient`).
 *
 * The grade ladder is deliberately ordered cheapest-first: the fallback for
 * a refused grade is the one below it. */
export type BackgroundGrade = 'flat' | 'gradient' | 'texture' | 'canvas';

/** THE MARK VOCABULARY (#1038 · S4). One spelling per kind, settled here
 * so that no two lanes can disagree about what a mark IS.
 *
 * WHY IT LIVES IN THIS FILE. `styles.ts` is the one client module that imports
 * nothing (three gates transpile it standalone and ensure-themes.mjs copies it
 * verbatim), and `AppStyle.marks` below is typed on `MarkKind` — so the type
 * has to sit at or below `styles.ts` in the import graph. `marks/_types.ts`
 * re-exports both names; every consumer should import from there.
 *
 * WHY A LIST AT ALL, given e2e/mark-kinds.mjs derives the census from the
 * emit-site literals and says "never from a list". Both are true and they
 * measure different things: the CENSUS answers "what does the tree stamp
 * today" (a fact about emitters, which must never be asserted), while this
 * is a closed TYPE — the set a renderer may be written for, which TypeScript
 * cannot derive from a runtime sweep. They are kept honest against each other
 * rather than trusted: `scripts/check-mark-refs.test.mjs` arm 3 REFUSES when the
 * source census names a kind this tuple does not hold, and REPORTS (never
 * refuses) the kinds declared here that nothing stamps yet. As of this
 * commit that report is `grid` and `axis` — both are drawn by
 * reporting-charts.ts and neither carries a `data-mark` stamp yet, which is
 * Wave 0-F's F2 remit, not this seat's.
 *
 * Spellings that are NOT in the vocabulary, and where they went:
 *   `sparkline` -> `spark`   (index-scaled sparkSvg)
 *   `gauge`     -> `ring`
 * `wave` is the time-windowed seismogram; it is NOT a synonym for `spark`. */
export type MarkKind =
  | 'area'
  | 'axis'
  | 'bar'
  | 'edge'
  | 'grid'
  | 'line'
  | 'meter'
  | 'node'
  | 'ring'
  | 'spark'
  | 'wave';

/** The vocabulary as data, sorted, so the seat's generated index and the gate
 * iterate in one deterministic order. */
export const MARK_KINDS: readonly MarkKind[] = [
  'area',
  'axis',
  'bar',
  'edge',
  'grid',
  'line',
  'meter',
  'node',
  'ring',
  'spark',
  'wave',
];

/** The ONE canvas the whole application may run. Not advice — style-axis.ts
 * enforces it at insert time and scripts/perf-budget.mjs refuses a second
 * registration with the offender named. A background is a full-viewport
 * always-on paint; two of them is the whole frame budget. */
export const MAX_BACKGROUND_CANVASES = 1;

/** The surface tiers a style dresses. Ordered outermost-in — and that order
 * is the perf contract: REFRACTION (backdrop-filter) is legal on `popover`
 * and `modal` (the CHROME tiers, at most a handful on screen at once) and on
 * the `shell` chrome, and is REFUSED on anything that renders per row, per
 * grid cell or per transcript message. perf-budget.mjs is that refusal. */
export const MATERIAL_TIERS = ['shell', 'card', 'popover', 'modal'] as const;
export type MaterialTier = (typeof MATERIAL_TIERS)[number];

/** The material properties every tier declares. One vocabulary for all four
 * tiers so a style is written as a 4×6 table, not as prose. */
export const MATERIAL_PROPS = ['bg', 'blur', 'brd', 'hi', 'glow', 'sh'] as const;
export type MaterialProp = (typeof MATERIAL_PROPS)[number];

/** `--mat-<tier>-<prop>` — the custom property a tier's material rides. */
export function materialVar(tier: MaterialTier, prop: MaterialProp): string {
  return `--mat-${tier}-${prop}`;
}

/** Cells of the tier × prop table that are RETIRED, declared BY NAME so the
 * table stays a readable 4×6 vocabulary with a named hole rather than a
 * silent 23. A cell is retired when no shipped style consumes it — #1971:
 * `--mat-shell-glow` was the last of Blueprint's value-`none` ingredients
 * with no consumer anywhere (GLASS's shell is a frame, not an instrument;
 * SYNTHWAVE's shell carries no glow by its own material rule; the other five
 * measured zero glow-shaped shell terms). A style that wants a retired cell
 * back removes it from this list AND adds its consuming rule in the SAME PR
 * — the smoke's `style tokens` arm refuses a declared-and-unconsumed token by
 * name, and the drift guard refuses a declaration the registry does not
 * hold. Every totality guard reads MATERIAL_VAR_NAMES, never 4×6. */
export const MATERIAL_RETIRED: readonly string[] = ['--mat-shell-glow'];

/** Every material custom property, in tier × prop order, minus the retired
 * cells. The styles.css style block must define all of them for every style
 * — totality is checked by the smoke drift guard and by perf-budget.mjs. */
export const MATERIAL_VAR_NAMES: readonly string[] = MATERIAL_TIERS.flatMap((t) =>
  MATERIAL_PROPS.map((p) => materialVar(t, p)),
).filter((n) => !MATERIAL_RETIRED.includes(n));

/** Background-layer custom properties. `--bgl-layer` is the CSS ground the
 * shell paints; the rest describe the layer element the texture/canvas
 * grades insert. */
export const BACKGROUND_VAR_NAMES: readonly string[] = [
  '--bgl-layer',
  '--bgl-attach',
  '--bgl-color',
  '--bgl-texture',
  '--bgl-texture-op',
  '--bgl-canvas-op',
];

/** FORM custom properties — the radius / stroke / grid / segment ladder.
 *
 * The third token family beside MATERIAL_VAR_NAMES (how a surface is MADE)
 * and BACKGROUND_VAR_NAMES (what sits at z-0): how a mark and a silhouette
 * are DRAWN. A style arm re-points a rung; nothing hard-codes a literal.
 *
 * DECLARATION SITE IS LOAD-BEARING AND IT IS NOT THE OBVIOUS ONE. These are
 * declared in the STYLE-AXIS base table (`:root, :root[data-style="blueprint"]`
 * at the end of styles.css), never in the bare `:root` at the top of the file.
 * helm-console/scripts/ensure-style-materials.mjs slices the stylesheet from
 * the `STYLE AXIS — material tiers` banner to EOF and copies the
 * custom-property declarations of every root-only rule into the console's
 * generated sheet — so a token declared above that banner never reaches the
 * console, the Bridge home, or any surface the console serves.
 *
 * The count is 24, not the 20 the seat issue's headline says: its own
 * enumeration is 10 radius + 7 stroke + 3 grid + 3 segment + 1 mark. The
 * enumeration is authoritative; the headline was arithmetic.
 *
 * Radius is NOT the meter axis — every meter already ships radius = height/2,
 * so the ladder's value is on cards, chips, modals, inputs and silhouette.
 * `--seg-pitch` is deliberately a PERCENTAGE: a percentage pitch is a count,
 * resolved at layout with no knowledge of the painted width, which is what
 * makes count-based segmentation reachable from a string-emitting call site.
 */
export const FORM_VAR_NAMES: readonly string[] = [
  // radius — 9 rungs plus the alias every existing `var(--radius)` resolves to
  '--radius-2xs',
  '--radius-xs',
  '--radius-sm',
  '--radius-md',
  '--radius-lg',
  '--radius-xl',
  '--radius-2xl',
  '--radius-pill',
  '--radius-full',
  '--radius',
  // stroke — widths are UNITLESS (an SVG stroke-width is user units, and a
  // `px` here would be wrong inside a viewBox); cap/join/miter are keywords
  '--stroke-hair',
  '--stroke-mark',
  '--stroke-icon',
  '--stroke-emph',
  '--stroke-cap',
  '--stroke-join',
  '--stroke-miter',
  // grid — the chart ground rule
  '--grid-step',
  '--grid-w',
  '--grid-dash',
  // segment — quantization of a linear mark
  '--seg-lit',
  '--seg-gap',
  '--seg-pitch',
  // mark — the SVG shape-rendering hint
  '--mark-rendering',
];

/** Form tokens DECLARED AHEAD OF THEIR CONSUMERS.
 *
 * #1035 declares the whole ladder where it crosses; the consumer sweep is a
 * separate unit (#1645 and its follow-ups). So these are registered and
 * Blueprint-declared while nothing reads them yet, and the smoke's style-token
 * arm — which gates `every registry token is consumed by a rule` — refuses all
 * of them at once without this set.
 *
 * This is NOT the `--mat-*` INGREDIENT_PIN. That one means "Blueprint value is
 * `none`, a style PR adds the consumer" and refuses a pin whose value is not
 * `none`; every token here carries a REAL value, so it cannot ride that list.
 *
 * TEETH IN BOTH DIRECTIONS, so this cannot rot into a blanket waiver: a form
 * token that is neither consumed nor listed here is refused by name, AND a
 * token listed here that GAINS a consumer is refused until it is removed. The
 * list may only SHRINK. It is an inventory pin of exactly the class #1856 P2
 * migrates to a seat directory; it lives here, in this PR's own new structure,
 * rather than as a row in a shared register, and migrates with the rest.
 */
export const FORM_DECLARED_AHEAD: readonly string[] = [
  // The five radius rungs (--radius-2xs/-xs/-2xl/-pill/-full) LEFT this list
  // with #2868, which migrated their single-value literals in both core
  // sheets; the four ladder steps left with #2204.
  '--stroke-hair',
  '--stroke-mark',
  '--stroke-icon',
  '--stroke-emph',
  '--stroke-cap',
  '--stroke-join',
  '--stroke-miter',
  '--grid-step',
  '--grid-w',
  '--grid-dash',
  '--seg-lit',
  '--seg-gap',
  '--seg-pitch',
];

/** The families FORM_VAR_NAMES owns, as a predicate over a custom-property
 * name. `scripts/form-tokens.test.mjs` uses this to census the assembled
 * stylesheet for a form token that is NOT in the registry — the arm the
 * Blueprint drift guard cannot make, because it only sees one block. */
export function isFormVarName(name: string): boolean {
  return /^--(?:radius|stroke|grid|seg)(?:-[a-z0-9]+)*$/.test(name) || name === '--mark-rendering';
}

export interface AppStyle {
  /** Stable id — the value of `data-style` on <html>. */
  id: string;
  /** Shown in the Settings picker. */
  label: string;
  /** One line under the label. */
  note: string;
  /** True for a style whose material block ships in styles.css and is pinned
   * by the drift guard (today: blueprint, and only blueprint). */
  builtin: boolean;
  /** The z-0 ground grade this style asks for. */
  background: BackgroundGrade;
  /** Whether this style's canvas grade (if any) is animated. A `canvas`
   * background that is not animated still costs a canvas but no rAF. */
  animated: boolean;
  /** The mark kinds this style re-cuts in its own idiom (#1038 · S4). `[]`
   * means "every mark falls to the Blueprint floor", which is the honest
   * state of every style at the time the seat landed. How many still sit
   * there is MEASURED and printed by `scripts/check-mark-refs.test.mjs`,
   * never written down here.
   *
   * BLUEPRINT IS PERMANENTLY `[]` — it IS the floor, so a per-style Blueprint
   * renderer would be a second copy of the thing it falls back to.
   * `scripts/check-mark-refs.test.mjs` arm 2 refuses a non-empty Blueprint
   * declaration by name, and refuses in BOTH directions between this field
   * and the seat directory: a kind declared here with no
   * `marks/<styleId>/<kind>.ts` is a slot that renders the floor while
   * claiming not to, and a seat file no style declares is dead art. */
  marks: readonly MarkKind[];
}

/** THE identity style. Its styles.css block maps every material token onto
 * the token that tier renders today, so selecting it is a no-op — which is
 * exactly what makes it safe to make it the default before any other style
 * exists. */
export const BLUEPRINT: AppStyle = {
  id: 'blueprint',
  label: 'Blueprint',
  note: "The shipped look — mission control at night, the chart table by day.",
  builtin: true,
  // Not `flat`: the shell has always painted a fixed radial
  // (styles.css `body`), so the honest identity grade is `gradient`. Calling
  // it flat would have made the identity instrument the thing that lied.
  background: 'gradient',
  animated: false,
  marks: [],
};

/** GLASS — the first non-identity style (PR-5), and the one that proves the
 * substrate end-to-end: real refraction on the chrome tiers, an animated
 * ground, a glow grammar, its own font kit and icon set.
 *
 * Thesis: calibrated optical instrumentation. Blur is HIERARCHY (shell 6 /
 * popover 20 / modal 28 px reads the z-order with every effect off), and
 * glow is replaced by DISPERSION — a spectral fringe whose three stops are
 * the theme's own --accent / --sea / --aux-2, so it tints for free.
 *
 * `builtin: true` carries its literal meaning here: the material table ships
 * in styles.css and is PINNED by a drift guard in the smoke, exactly as
 * Blueprint's is. A style whose table could drift silently would make every
 * contrast measurement below it worthless.
 *
 * The ground is `canvas` — five badly-defocused discs of theme-tinted light,
 * peak alpha .10, drifting on 60–140 s sine paths. It rides PR-4's ONE
 * canvas: glass-bokeh.ts registers a painter and the substrate owns the
 * element, the cap, the rAF and pause-on-hidden. Under either reduce lane
 * effectiveGrade() degrades it to `gradient` and the canvas is never
 * constructed; styles.css paints the same five discs at t = 0 instead. */
export const GLASS: AppStyle = {
  id: 'glass',
  label: 'Glass',
  note: 'Coated instrument glass over a cosmic ground — blur is hierarchy, dispersion replaces glow.',
  builtin: true,
  background: 'canvas',
  animated: true,
  marks: [],
};

/** SYNTHWAVE '88 — LINE-WORK, NOT LIGHTING (PR-11), and the deliberate
 * opposite pole from GLASS on the one axis that matters most: the material.
 *
 * Thesis: the arcade cabinet AT REST, not mid-attract-loop. Panels are FLAT
 * OPAQUE PLATES defined entirely by their border; the glow is a bloom
 * OUTSIDE an opaque edge, never a haze THROUGH a translucent one.
 *
 *   NO `backdrop-filter`. NO translucent fill. NO refraction, anywhere.
 *   Alpha is legal in `box-shadow` and nowhere else — never on a shape's own
 *   background.
 *
 * That is the owner's distinctness ruling (R15: dispersion is Glass's, an
 * opaque outer bloom is Synthwave's) and it is GATE-PROVABLE rather than
 * aspirational: the smoke greps this style's scope for `backdrop-filter`, for
 * `rgba(` inside a background, and for `opacity` on a panel selector, prints
 * the count scanned, and demonstrates the lint REFUSING a planted frosted
 * card before its pass is trusted.
 *
 * Tier is carried by a BORDER-WEIGHT LADDER (1.5px card → 2px popover/modal)
 * rather than by glow, so z-order still reads with every `box-shadow` off —
 * that is the acceptance criterion, not a nicety. Glow marks STATE: only an
 * active / selected / live surface is lit (R16, ≤4 lit surfaces per view,
 * also gate-proven), icons are unlit at rest, and nothing pulses, breathes or
 * flickers.
 *
 * The ground is `canvas` — a Battlezone perspective grid receding in Z on a
 * 12 s loop under a hard horizon at 38% (R13: motion in Z is Synthwave's,
 * XY translation is Starship's, rotation is Destroyer's). It rides PR-4's ONE
 * canvas via synthwave-grid.ts; under either reduce lane effectiveGrade()
 * degrades it to `gradient`, the canvas is never constructed, and styles.css
 * paints the same geometry as a static frame instead.
 *
 * `builtin: true` carries its literal meaning: the material table ships in
 * styles.css and is PINNED by a drift guard in the smoke, exactly as
 * Blueprint's and GLASS's are. */
export const SYNTHWAVE: AppStyle = {
  id: 'synthwave',
  label: "Synthwave '88",
  note: 'Flat opaque plates, neon line-work and an outer bloom over a perspective grid — glow marks state, never chrome.',
  builtin: true,
  background: 'canvas',
  animated: true,
  marks: [],
};

/** QUARTERDECK — the navigation instrument (PR-8).
 *
 * Thesis: the chart table at three bells into the middle watch. The Bridge is
 * an INSTRUMENT, not a pirate stage — no skulls, no parchment, no rope, no
 * wood photography. Two guardrails carry the whole style: cool-neutral
 * grounds with BRASS AS THE ONE WARM NOTE reserved for the accent (ruling
 * R4 — brass is QUARTERDECK's exclusively), and age carried entirely by
 * LINEWORK, never by warm-stain colour (the light slot's ink is lamp-black
 * `#1C1D18` and never browns).
 *
 * `builtin: true` for the same reason GLASS carries it: the material table
 * ships in styles.css and is PINNED by a drift guard in the smoke, so the
 * contrast numbers measured under it stay the numbers that render.
 *
 * The ground is `canvas`, and it is ONE canvas carrying two things that must
 * never be confused with a neighbouring style's (rulings R2.2/R12): a STATIC
 * engraved rhumb field — it never rotates, that is Destroyer's — lit by ONE
 * moving warm light on a 14 s pendulum arc, which never blurs anything, that
 * is Glass's. qd-chart.ts registers a painter against PR-4's single canvas;
 * under either reduce lane effectiveGrade() degrades it to `gradient`, the
 * canvas is never constructed, and styles.css paints the same rhumb field
 * plus one frozen lantern pool as a still. */
export const QUARTERDECK: AppStyle = {
  id: 'quarterdeck',
  label: 'Quarterdeck',
  note: 'The chart table at three bells — engraved linework under a swinging lantern, brass the one warm note.',
  builtin: true,
  background: 'canvas',
  animated: true,
  marks: [],
};

/** STARSHIP — the disciplined deck (PR-6).
 *
 * Thesis: a well-run bridge is QUIET. Every readout is legible at a glance,
 * every light means one specific thing, and nothing moves unless a state
 * actually changed. Two rules define it and nothing may violate them:
 *
 *   1. THE DRIFT IS THE ONLY THING THAT MOVES without user input, anywhere
 *      in the app. One canvas, one rigid translation along a fixed heading.
 *      No sweeps, no pulses, no breathing seams, no boot flickers — which is
 *      why this style's block switches OFF the `breathe` animation the pill
 *      dots ride under Blueprint rather than adding one of its own.
 *   2. SETTLE-ONCE GLOW. Glow marks a TRANSITION; plain colour marks a
 *      STATE. Nothing glows forever — the resting `box-shadow: 0 0 8px` on a
 *      working status dot is removed under this style, and no tier carries a
 *      resting glow at all.
 *
 * Materially the inverse of GLASS: THE SHELL IS OPAQUE (a 2px blur behind a
 * 98%-opaque fill is a promoted compositing layer that can never show its
 * effect), so the ground reads AROUND the chrome rather than through it —
 * light seen through a window, not through a lens (cross-style ruling R2).
 * Refraction rises with elevation and transience instead: popover 16px,
 * modal 24px, and the dense card tier none at all.
 *
 * The ground is `canvas` — ~450 hard, in-focus, ACHROMATIC points translating
 * as one rigid field along a fixed heading (R2/R12/R13: motion in XY, no
 * rotation, no defocus, one hue for the whole field). It rides PR-4's ONE
 * canvas through the painter registry; under either reduce lane the canvas is
 * never constructed and styles.css paints a static tiled field instead. */
export const STARSHIP: AppStyle = {
  id: 'starship',
  label: 'Starship',
  note: 'A quiet bridge under way — opaque deck plating over a translating starfield, glow spent once.',
  builtin: true,
  background: 'canvas',
  animated: true,
  marks: [],
};

/** STUDIO — the quiet style (PR-10), and the proof the axis handles minimal.
 *
 * Thesis: restraint that is ITEMIZED. The failure mode of every "minimal"
 * system is that restraint and neglect look identical in a screenshot, so
 * every atom here has a stated reason — a zero-chroma neutral ramp (R = G = B
 * to the byte), a four-step radius ladder used nowhere arbitrarily, a
 * 2 -> 3 -> 4-layer shadow escalation in light and a DIFFERENT elevation
 * model in dark (a black shadow on a #0B0B0B ground has no luminance left to
 * remove), and exactly one texture, which is a FIX rather than a decoration.
 *
 * `background: 'texture'` and NOT `'flat'`, and that distinction is the same
 * one Blueprint's `gradient` records: the ground IS flat — no grid, no dots,
 * no gradient, no light, no geometry — but STUDIO does ship one overlay, a
 * static 128x128 grain tile at 1.5% opacity in DARK SLOTS ONLY, which exists
 * to kill the banding a perfectly flat grey shows on low-light OLED panels.
 * Declaring `flat` while shipping a tile would make the registry the thing
 * that lies. The `texture` grade inserts the ONE layer element and nothing
 * else: no canvas, no rAF, no painter — STUDIO and MAINFRAME are the two
 * styles in the programme that mount neither, and the smoke drives the real
 * substrate machinery to measure that zero with GLASS as its positive
 * control.
 *
 * The one animated element in the whole style is the plotter-pen line-draw
 * on the pending affordance, and it REPLACES the app's rotating spinner
 * rather than adding to it. It is CSS-only and collapses under
 * prefers-reduced-motion.
 *
 * `builtin: true` for the same reason GLASS carries it: the material table
 * ships in styles.css and is drift-pinned by the smoke. */
export const STUDIO: AppStyle = {
  id: 'studio',
  label: 'Studio',
  note: 'Cold, matte, wipe-clean — one shared neutral ground, and the craft is all in the edges.',
  builtin: true,
  background: 'texture',
  animated: false,
  marks: [],
};

/** MAINFRAME '84 — the terminal you leave running for eight hours, not the
 * one you screenshot once (PR-9).
 *
 * Thesis in three rules, and nothing in the style may violate them:
 *   1. ONLY THE GLASS GLOWS. Phosphor bloom exists on exactly one surface in
 *      the whole application — the chat/terminal viewport bezel, the one
 *      thing that is diegetically a screen. Cards, popovers, modals and
 *      tables are PAPER: flat, matte, unglowing.
 *   2. NOTHING MOVES ON ITS OWN. A CSS-only ground grade and
 *      `animated: false` — no canvas, no rAF, no ambient animation of any
 *      kind. MAINFRAME is the ONE style in the programme that registers no
 *      canvas painter at all (R12), and the smoke measures that ABSENCE with
 *      a positive control rather than asserting it. The motion budget is
 *      spent entirely on semantics: the block cursor blinks because
 *      something is live.
 *   3. INTENT IS CARRIED BY INTENSITY AND INVERSION BEFORE HUE. On a
 *      monochrome field hue is not available: dim vs bright is the
 *      neutral/additive channel (R0.1 narrowed — the ink stack IS the
 *      theme's hue here, so `--ink-2` "hueless" does not exist and neutral
 *      is `--ink-3`), and reverse video is the destructive/error channel
 *      (R0.3 narrowed, MAINFRAME's exclusively).
 *
 * `builtin: true` in its literal sense: the material table ships in
 * styles.css and is pinned by a drift guard in the smoke, exactly as
 * Blueprint's and GLASS's are.
 *
 * THE GROUND IS `gradient`, AND THAT IS A DELIBERATE CHOICE OVER `texture`.
 * MAINFRAME's field is a static full-field raster, which the `texture` grade
 * exists to carry — but that grade INSERTS `#style-bg`, a `position: fixed;
 * z-index: 0` element prepended to `<body>`. A positioned z-index:0 box
 * paints in step 6 of the root stacking context, i.e. ABOVE the
 * non-positioned block content of `main` (which is not positioned and whose
 * `overflow-y: auto` does not make a stacking context). An opaque
 * `--bgl-color` on that layer would therefore cover the application. The
 * `gradient` grade inserts NOTHING: the vignette and the scanline raster
 * ride `body`'s own multi-layer `background-image`, which paints in step 1
 * and can never occlude anything, and `background-attachment: fixed` keeps
 * the raster viewport-pinned so it never moirés against a scrolling
 * transcript. Cheapest grade, zero inserted nodes, zero rAF — which is R12's
 * claim for this style stated in the substrate's own vocabulary. */
export const MAINFRAME: AppStyle = {
  id: 'mainframe',
  label: "Mainframe '84",
  note: 'Flat ops paper with one pane of phosphor glass — dim/bright is the intent channel, and nothing moves on its own.',
  builtin: true,
  background: 'gradient',
  animated: false,
  marks: [],
};


/** DESTROYER — PR-7. Naval signal doctrine as a material system, and the
 * style that proves the substrate can carry a SECOND non-identity style
 * without either one leaking into the other.
 *
 * Thesis: flat armor. Depth is bevel plus directional shadow, never ambient
 * glass — refraction is EARNED and appears on exactly two surfaces (the
 * popover, and the modal's SCRIM, because a confirm dialog's panel must be
 * opaque and it is the world behind it that goes inert). Because the Bridge's
 * buttons are icon-only, SHAPE is the primary intent channel and colour only
 * confirms; that inversion is what lets this style carry Battle Lanterns, the
 * programme's one red-night theme, where hue is removed as a channel
 * entirely and the status ladder rides a 7-rung LUMINANCE ramp instead.
 *
 * `builtin: true` for the same reason GLASS carries it: the material table
 * ships in styles.css and is pinned by a drift guard in the smoke. Every
 * number scripts/style-contrast.mjs measures is read out of that block.
 *
 * The ground is `canvas`, and it is the cheapest one in the programme: the
 * etched deck plate, the range rings (an UNBOUNDED repeating gradient, so the
 * ladder fills the field) and the 360-tick bearing scale are all STATIC CSS,
 * and the canvas carries ONE hairline bearing line — at `--dz-sweep-alpha`
 * (.24), turning once every 24 s, over the WHOLE field: its radius is the
 * distance from the origin (72% / 102%) to the farthest corner, not a
 * constant (owner rulings 2026-09-08 "the sweep must be seen" and 2026-09-09
 * "sized to match the background"). One clear, one conic fill and one stroke
 * per frame; the alpha ceilings are what keep it out of the way of live data,
 * and style-contrast composites both the LINE (point) and the TRAIL (field)
 * under text. It rides PR-4's
 * one canvas through a registered painter, so the cap, the rAF and
 * pause-on-hidden are the substrate's. Under either reduce lane
 * effectiveGrade() degrades it to `gradient`, the canvas is never
 * constructed, and styles.css paints the same instrument with the bearing
 * line frozen at 000. */
export const DESTROYER: AppStyle = {
  id: 'destroyer',
  label: 'Destroyer',
  note: 'Flat armor and signal doctrine — shape is the intent channel, colour only confirms.',
  builtin: true,
  background: 'canvas',
  animated: true,
  marks: [],
};
export const STYLES: AppStyle[] = [BLUEPRINT, GLASS, SYNTHWAVE, QUARTERDECK, STARSHIP, STUDIO, MAINFRAME, DESTROYER];

export const DEFAULT_STYLE_ID = 'blueprint';

const byId = new Map(STYLES.map((s) => [s.id, s]));

/** Every style id, in registry order. */
export function styleIds(): string[] {
  return STYLES.map((s) => s.id);
}

/** Map a stored/unknown id onto a real one. Unknown ids fall back to
 * Blueprint — a style that has been removed must degrade to the identity
 * look, never to an unstyled document. */
export function normalizeStyleId(id: string | undefined): string {
  return id && byId.has(id) ? id : DEFAULT_STYLE_ID;
}

/** Resolve a stored id to a style. Never returns undefined. */
export function resolveStyle(id: string | undefined): AppStyle {
  return byId.get(normalizeStyleId(id)) ?? BLUEPRINT;
}

// ---------------------------------------------------------------------
// THE DARK-LAUNCH SEAT
// ---------------------------------------------------------------------
//
// The whole style axis ships DARK. The substrate is live on every surface —
// the attribute is stamped, the material tokens resolve, the background
// machinery is wired — and the Settings picker is HIDDEN, so the only style
// anyone can be on is the identity one. Nothing renders differently.
//
// PR-9 ("flip the seat") is this one line: the constant is now `true`, so the
// Settings picker is LIVE and offers all styles. Blueprint stays
// DEFAULT_STYLE_ID, so no one's look changes until they actively pick — the
// flip only EXPOSES the picker. Rollback is the same line reversed. Deliberately
// a source constant and not a config file: launch is a deploy, and a deploy is
// a merge here.
//
// Two override lanes still exist: the per-Bridge `prefs.stylePicker` server
// pref can switch the picker OFF for a single Bridge (undefined ≠ false — a
// Bridge that never heard of the seat falls through to this constant), and the
// per-session `?style-picker=off` lane can hide it for one tab — see
// style-axis.ts resolveStyleSeat() for the precedence and the boot line that
// names which lane won.

/** LAUNCHED (DEFAULT-ON). The one line PR-9 flipped; rollback is `= false`. */
export const STYLE_PICKER_SEAT_DEFAULT = true;

/** Query param / sessionStorage key for the per-session override lane. */
export const STYLE_SEAT_PARAM = 'style-picker';
export const STYLE_SEAT_SESSION_KEY = 'helm-style-picker';

/** Where a resolved seat value came from. Printed at boot — an unnamed
 * source is how a dark-launched feature quietly goes live. */
export type StyleSeatSource = 'default' | 'server-pref' | 'session-override';

export interface StyleSeat {
  enabled: boolean;
  source: StyleSeatSource;
}

/**
 * Resolve the seat from its three inputs, highest precedence first. Pure —
 * the DOM/storage reads happen in style-axis.ts and are passed in, so this
 * decision is unit-testable and is in fact exercised by the smoke.
 *
 *   1. session override  — the owner-checkpoint lane (?style-picker=on),
 *                          sticky for the tab via sessionStorage
 *   2. server pref       — `prefs.stylePicker` on the Bridge's config.json,
 *                          PRESENT-or-absent (undefined ≠ false): a Bridge
 *                          that has never heard of the seat must not be read
 *                          as a Bridge that switched it off
 *   3. the constant above
 */
export function resolveStyleSeat(input: {
  sessionOverride?: boolean | undefined;
  serverPref?: boolean | undefined;
}): StyleSeat {
  if (input.sessionOverride !== undefined) {
    return { enabled: input.sessionOverride, source: 'session-override' };
  }
  if (input.serverPref !== undefined) {
    return { enabled: input.serverPref, source: 'server-pref' };
  }
  return { enabled: STYLE_PICKER_SEAT_DEFAULT, source: 'default' };
}

/**
 * The styles a picker may OFFER. With the seat off this is exactly
 * `[blueprint]` — the picker has nothing to pick, which is why the UI hides
 * it rather than rendering a one-option dropdown.
 *
 * This is the choke point: every surface that renders a picker asks this
 * function, so "the seat is off" cannot be true in one surface and false in
 * another.
 */
export function offeredStyles(seatEnabled: boolean): AppStyle[] {
  return seatEnabled ? STYLES : STYLES.filter((s) => s.id === DEFAULT_STYLE_ID);
}
